import { useEffect, useState } from 'react';

import { personaService } from '@/shared/api';
import type { PersonaData, UpdatePersonaRequest } from '@/shared/api/persona-service';
import { useInfinitePersonas } from '@/shared/hooks/use-infinite-personas';
import { useModalStore } from '@/shared/stores/modal-store';
import { BaseModal, Button, CustomInput } from '@/shared/ui';

export const EditPersonaModal = () => {
  const { isModalOpen, closeModal, getModalData } = useModalStore();
  const { personas, refetch, deletePersonaFromCache } = useInfinitePersonas();

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only name is editable now
  const [name, setName] = useState('');

  const isOpen = isModalOpen('edit-persona');
  const modalData = getModalData('edit-persona');
  const personaId = (modalData as { persona?: { id: number } })?.persona?.id;

  // Get current persona data from React Query cache instead of modal data
  const persona: PersonaData | null = personaId
    ? personas.find((p) => p.id === personaId) || null
    : null;

  // Helper function to format display values
  const formatValue = (value: string | number | undefined) => {
    if (!value) return 'Not specified';
    if (typeof value === 'string') {
      // Capitalize first letter for better display
      return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
    }
    return value.toString();
  };

  // Update name when persona data changes
  useEffect(() => {
    if (persona) {
      setName(persona.name || '');
    }
  }, [persona]);

  const handleClose = () => {
    closeModal('edit-persona');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!persona) return;

    try {
      setIsLoading(true);
      setError(null);

      // Check if name has changed
      const hasChanges = name !== persona.name;

      if (hasChanges) {
        const updateData: UpdatePersonaRequest = { name: name };

        await personaService.updatePersona(persona.id, updateData);

        // Refetch personas to get updated data from server
        await refetch();
      }

      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update model');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!persona) return;

    if (
      !window.confirm(
        `Are you sure you want to delete "${persona.name || 'this model'}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setIsDeleting(true);
      await personaService.deletePersona(persona.id);
      deletePersonaFromCache(persona.id);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete model');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!persona) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Model"
      size="md"
      className="!max-h-[90vh] !w-[600px] !max-w-[600px]"
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4 w-full max-h-[calc(90vh-120px)] overflow-y-auto px-2"
      >
        {/* Model Name - Editable */}
        <div>
          <CustomInput
            label="Model Name"
            placeholder="Enter model name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!name ? 'Name is required' : undefined}
          />
        </div>

        {/* Model Properties - Read Only */}
        <div className="-mt-2">
          <div className="grid grid-cols-2 gap-x-10 gap-y-2 pb-10">
            {/* Left Column - 4 items */}
            <div className="space-y-2">
              <div className="text-sm">
                <span className="text-zinc-400">Gender:</span>
                <span className="text-white font-medium ml-1">{formatValue(persona.gender)}</span>
              </div>

              <div className="text-sm">
                <span className="text-zinc-400">Age:</span>
                <span className="text-white font-medium ml-1">{formatValue(persona.age)}</span>
              </div>

              <div className="text-sm">
                <span className="text-zinc-400">Hair Color:</span>
                <span className="text-white font-medium ml-1">
                  {formatValue(persona.hairColor)}
                </span>
              </div>

              <div className="text-sm">
                <span className="text-zinc-400">Hair Style:</span>
                <span className="text-white font-medium ml-1">
                  {formatValue(persona.hairStyle)}
                </span>
              </div>
            </div>

            {/* Right Column - 3 items */}
            <div className="space-y-2">
              <div className="text-sm">
                <span className="text-zinc-400">Eye Color:</span>
                <span className="text-white font-medium ml-1">{formatValue(persona.eyeColor)}</span>
              </div>

              <div className="text-sm">
                <span className="text-zinc-400">Body Type:</span>
                <span className="text-white font-medium ml-1">{formatValue(persona.bodyType)}</span>
              </div>

              <div className="text-sm">
                <span className="text-zinc-400">Ethnicity:</span>
                <span className="text-white font-medium ml-1">
                  {formatValue(persona.ethnicity)}
                </span>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          {persona.additionalDetails && (
            <div className="mt-3 pt-3 border-t border-zinc-700">
              <div className="text-sm">
                <span className="text-zinc-400">Description:</span>
                <span className="text-white font-medium ml-1 block mt-1">
                  {persona.additionalDetails}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            type="button"
            onClick={handleDelete}
            disabled={isLoading || isDeleting}
            variant="danger"
            size="md"
            className="order-2 sm:order-1"
          >
            {isDeleting ? 'Deleting...' : 'Delete Model'}
          </Button>
          <Button
            type="submit"
            disabled={isLoading || isDeleting || !name}
            variant="primary"
            size="md"
            className="order-1 sm:order-2 flex-1"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
};
